import {useStripe, useElements, PaymentElement, AddressElement} from '@stripe/react-stripe-js';
import React, {useEffect, useRef, useState} from 'react';
import {Button, CircularProgress, Typography} from "@material-ui/core";
import _ from 'lodash/fp';

const STRIPE_REDIRECT_PARAMS = [
    'payment_intent',
    'payment_intent_client_secret',
    'redirect_status',
]

const getStripeReturnUrl = () => {
    const url = new URL(window.location.href)
    STRIPE_REDIRECT_PARAMS.forEach(param => url.searchParams.delete(param))
    return url.toString()
}

const CheckoutForm = ({
    amount,
    teamId,
    teamName,
    transactionType,
    createStripeConfirmationQuote = _.noop,
    finalizeStripePayment = _.noop,
    onStripePaymentStarted = _.noop,
    onStripePaymentCompleted = _.noop,
    onStripePaymentFailed = _.noop,
    onStripePaymentPending = _.noop,
    payProcessingFees = true,
}) => {
    const stripe = useStripe();
    const elements = useElements();
    const [quote, setQuote] = useState(null)
    const [errorMessage, setErrorMessage] = useState(null)
    const [isBusy, setBusy] = useState(false)
    const isMounted = useRef(true)

    useEffect(() => () => {
        isMounted.current = false
    }, [])

    useEffect(() => {
        setQuote(null)
    }, [amount, payProcessingFees])

    const handleSubmit = async (event) => {
        let isFinalizingPayment = false
        // We don't want to let default form submission happen here,
        // which would refresh the page.
        event.preventDefault();

        if (!stripe || !elements) {
            // Stripe.js hasn't yet loaded.
            // Make sure to disable form submission until Stripe.js has loaded.
            return;
        }

        setBusy(true)
        setErrorMessage(null)

        try {
            if (!quote) {
                const submitResult = await elements.submit()
                if (submitResult.error) {
                    throw new Error(submitResult.error.message)
                }

                const confirmationTokenResult = await stripe.createConfirmationToken({elements})
                if (confirmationTokenResult.error) {
                    throw new Error(confirmationTokenResult.error.message)
                }

                const nextQuote = await createStripeConfirmationQuote({
                    confirmationTokenId: confirmationTokenResult.confirmationToken.id,
                    amount,
                    teamId,
                    teamName,
                    transactionType,
                })
                if (!nextQuote) {
                    throw new Error('Unable to calculate the Stripe processing fee')
                }
                const adjustedQuote = payProcessingFees ? {
                    ...nextQuote,
                    processingFeesPaid: true,
                } : {
                    ...nextQuote,
                    feeAmount: 0,
                    grossAmount: amount,
                    feePolicy: 'NO_FEE',
                    rate: 0,
                    fixedFee: 0,
                    processingFeesPaid: false,
                }
                setQuote({
                    ...adjustedQuote,
                    confirmationTokenId: confirmationTokenResult.confirmationToken.id,
                })
                return
            }

            isFinalizingPayment = true
            onStripePaymentStarted()
            const paymentIntent = await finalizeStripePayment({
                ...quote,
                returnUrl: getStripeReturnUrl(),
            })
            if (!paymentIntent) {
                throw new Error('Unable to finalize the Stripe payment')
            }
            let completedPaymentIntent = paymentIntent
            if (paymentIntent.status === 'requires_action') {
                await onStripePaymentPending()
                const nextActionResult = await stripe.handleNextAction({clientSecret: paymentIntent.clientSecret})
                if (nextActionResult.error) {
                    throw new Error(nextActionResult.error.message)
                }
                completedPaymentIntent = nextActionResult.paymentIntent
            }

            if (completedPaymentIntent?.status !== 'succeeded') {
                throw new Error('The payment was not completed. Please try again.')
            }

            await onStripePaymentCompleted()
        } catch (error) {
            if (isFinalizingPayment) {
                onStripePaymentFailed()
            }
            if (isMounted.current) {
                if (isFinalizingPayment) {
                    setQuote(null)
                }
                setErrorMessage(error.message)
            }
        } finally {
            if (isMounted.current) {
                setBusy(false)
            }
        }
    };

    return (
        <div>
            <PaymentElement />
            <AddressElement options={{mode: 'billing'}} />
            {quote &&
                <Typography style={{margin: '1rem 0 0 0'}}>
                     <span style={{fontSize: '1.25rem', fontWeight: 'bold'}}>Stripe total: HK${Number(quote.grossAmount).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span> {payProcessingFees ? `(includes estimated HK$${Number(quote.feeAmount).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})} processing fee)` : '(excludes processing fee)'}
                </Typography>
            }
            {errorMessage && <Typography color="error" style={{marginTop: '1rem'}}>{errorMessage}</Typography>}
            <Button onClick={handleSubmit} color="primary" size="large" style={{marginTop: "1rem"}} variant="contained" disabled={!stripe || isBusy}>
                {isBusy ? <CircularProgress size={24}/> : quote ? 'Confirm Payment' : 'Review Payment Total'}
            </Button>
        </div>
    )
};

export default CheckoutForm;